import { Combobox } from '@base-ui/react/combobox';
import { darken, rgba } from 'polished';
import styled from 'styled-components';

import { inputPlaceholderStyle } from '@components/select/styles';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

export const Chips = styled(Combobox.Chips)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: pxToRem(4),
  padding: `${pxToRem(4)} ${pxToRem(4)} ${pxToRem(4)} ${pxToRem(8)}`,
  flex: 1,
  minWidth: 0,
});

const pendingRemovalBorderColor = (color?: string) =>
  color ? darken(0.1, color) : theme.form.value.multi.colorHover;

export const Chip = styled(Combobox.Chip)<{ $color?: string }>((p) => ({
  display: 'inline-flex',
  alignItems: 'center',
  minWidth: 0,
  maxWidth: '100%',
  border: `0.0625rem solid ${theme.form.value.multi.border.color}`,
  borderRadius: pxToRem(4),
  padding: `${pxToRem(4)} ${pxToRem(4)} ${pxToRem(4)} ${pxToRem(8)}`,
  backgroundColor: theme.form.value.multi.bg.color,
  color: theme.form.value.multi.color,
  font: theme.font.semibold12,
  cursor: 'default',
  transition: transitionStandard('border-color', 'box-shadow'),
  svg: {
    color: theme.form.value.multi.color,
  },
  ...(p.$color && {
    borderColor: p.$color,
    color: p.$color,
    backgroundColor: rgba(p.$color, 0.1),
    svg: { color: p.$color },
  }),
  '&[data-pending-removal]': {
    borderColor: pendingRemovalBorderColor(p.$color),
    boxShadow: `inset 0 0 0 0.0625rem ${pendingRemovalBorderColor(p.$color)}`,
  },
  // A label with no break opportunity (an email address) wraps anywhere
  // rather than truncating, so the whole value stays readable.
  '> span:first-child': {
    marginRight: pxToRem(4),
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
}));

export const ChipsInput = styled(Combobox.Input)<{ $isCaretHidden: boolean }>((p) => ({
  flex: 1,
  minWidth: '4rem',
  border: 'none',
  background: 'transparent',
  outline: 'none',
  font: 'inherit',
  color: 'inherit',
  ...(p.$isCaretHidden && { caretColor: 'transparent' }),
  ...inputPlaceholderStyle,
}));

export const ChipRemove = styled(Combobox.ChipRemove)<{ $color?: string }>((p) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  background: 'transparent',
  border: 'none',
  borderRadius: pxToRem(2),
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  color: 'inherit',
  '&[data-disabled]': {
    cursor: 'not-allowed',
  },
  '&:not([data-disabled]):hover': p.$color
    ? { backgroundColor: rgba(p.$color, 0.2) }
    : {
        backgroundColor: theme.form.value.multi.bg.colorHover,
        svg: { color: theme.form.value.multi.colorHover },
      },
}));
